import type { TimeDrumGroup } from "@/shared/ui"

import { minutesOfDayInTz } from "./timeGrid"

/** Projects a UTC instant to local { hour, minute } in the given timezone. */
export function localTimeOfInstant(
  isoUtc: string,
  tz: string,
): { hour: number; minute: number } {
  const totalMin = minutesOfDayInTz(isoUtc, tz)
  return { hour: Math.floor(totalMin / 60), minute: totalMin % 60 }
}

/**
 * Groups UTC availability slots by local hour for the TimeDrum component.
 * Slots within the same local hour are sorted by minute.
 */
export function groupSlotsByLocalTime(slots: string[], tz: string): TimeDrumGroup[] {
  const map = new Map<number, number[]>()

  for (const slot of slots) {
    const { hour, minute } = localTimeOfInstant(slot, tz)
    if (!map.has(hour)) map.set(hour, [])
    map.get(hour)!.push(minute)
  }

  const groups: TimeDrumGroup[] = []
  for (const [hour, minutes] of map) {
    groups.push({
      hour,
      minutes: [...new Set(minutes)].sort((a, b) => a - b),
    })
  }

  return groups.sort((a, b) => a.hour - b.hour)
}

/**
 * Finds the UTC instant in `slots` that projects to the given local hour:minute.
 * Returns `undefined` if no matching slot exists.
 */
export function instantForLocalTime(
  slots: string[],
  tz: string,
  hour: number,
  minute: number,
): string | undefined {
  return slots.find((slot) => {
    const { hour: h, minute: m } = localTimeOfInstant(slot, tz)
    return h === hour && m === minute
  })
}
